import type { FC } from "react"
import React from "react"
import type { GetServerSideProps, GetServerSidePropsResult } from "next"

import ErrorBoundary from "common/components/ErrorBoundary"
import MainLayout from "ui/layouts/main/MainLayout"
import MainLayoutHead from "ui/layouts/main/MainLayoutHead"
import MainLayoutContainer from "ui/layouts/main/MainLayoutContainer"
import MainLayoutFooter from "ui/layouts/main/MainLayoutFooter"
import type { ChainId, Governor, Organization } from "query/graphql"
import type { DescriptionException } from "proposal/types/exception"
import GovernanceProposalsList from "governance/components/GovernanceProposalsList"
import { getGovernanceIds } from "governance/helpers/governance"
import {
  getOrganizationDescription,
  getOrganizationSocialPreviewImage,
} from "organization/helpers/organization"
import { fetchDescriptionExceptionsByGovernorId } from "proposal/helpers/description"
import { DescriptionExceptionsProvider } from "proposal/providers/DescriptionExceptionsProvider"
import { BREADCRUMB } from "common/constants/breadcrumb"
import { isValidAccountId } from "web3/helpers/accountId"
import { fetchGovernanceById } from "governance/helpers/fetch"
import { ROUTES } from "common/constants/routes"
import {
  fetchGovernorsByOrganization,
  fetchOrganizationBySlug,
} from "organization/helpers/fetcher"
import GovernanceProvider from "governance/context"
import { getWhitelabelBreadcrumb } from "whitelabel/utils/breadcrumb"
import { isCorrectSlug, isWhitelabel } from "whitelabel/hooks/useIsWhiteLabel"
import GovernanceProposalsNavTabs from "governance/components/GovernanceProposalsNavTabs"
import { OrganizationProvider } from "organization/providers/OrganizationProvider"
import { OrganizationMenu } from "ui/layouts/main/header/HeaderDAO"
import MainLayoutHeader from "ui/layouts/main/MainLayoutHeader"
import DaoLayoutBreadcrumb from "ui/layouts/dao/DaoLayoutBreadcrumb"

type Props = {
  chainId: ChainId
  governors: Governor[]
  isWhiteLabel: boolean
  descriptionExceptions: DescriptionException[]
  organization: Organization
}

const ProposalsList: FC<Props> = ({
  chainId,
  governors,
  isWhiteLabel,
  descriptionExceptions,
  organization,
}) => {
  const [governor] = governors
  const { slug, name } = organization
  const governorIds = getGovernanceIds(governors)

  const title = isWhiteLabel
    ? `${name} | Proposals`
    : `Tally | ${name} Proposals`
  const preview = {
    title,
    image: getOrganizationSocialPreviewImage(organization),
    description: getOrganizationDescription(organization),
  }

  const breadcrumb = isWhiteLabel
    ? getWhitelabelBreadcrumb(
        BREADCRUMB.governance.proposals.list(slug, name, isWhiteLabel),
      )
    : BREADCRUMB.governance.proposals.list(slug, name, isWhiteLabel)

  return (
    <ErrorBoundary section="Governance > Proposals">
      <DescriptionExceptionsProvider
        descriptionExceptions={descriptionExceptions}
      >
        <OrganizationProvider organization={organization}>
          <GovernanceProvider governor={governor}>
            <MainLayout>
              <MainLayoutHead preview={preview} title={title} />
              <MainLayoutHeader
                activeOrganizationMenu={OrganizationMenu.Proposals}
                isWhiteLabel={isWhiteLabel}
                organization={organization}
              />
              <DaoLayoutBreadcrumb breadcrumb={breadcrumb} />
              <MainLayoutContainer>
                <ErrorBoundary section="GovernanceProposalsNavTabs">
                  <GovernanceProposalsNavTabs
                    borderBottomRadius={0}
                    borderBottomWidth={0}
                    slug={organization.slug}
                  />
                </ErrorBoundary>
                <ErrorBoundary section="GovernanceProposalsList">
                  <GovernanceProposalsList
                    displayCreateProposal
                    chainId={chainId}
                    displayTop={false}
                    governorIds={governorIds}
                    isWhiteLabel={isWhiteLabel}
                    organization={organization}
                    slug={slug}
                  />
                </ErrorBoundary>
              </MainLayoutContainer>
              <MainLayoutFooter isWhiteLabel={isWhiteLabel} />
            </MainLayout>
          </GovernanceProvider>
        </OrganizationProvider>
      </DescriptionExceptionsProvider>
    </ErrorBoundary>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context,
): Promise<GetServerSidePropsResult<Props>> => {
  const { query } = context
  const { governanceId: governanceIdParam } = query
  const slug = governanceIdParam as string

  // Find governance by id and redirect to /gov/:slug
  if (isValidAccountId(slug)) {
    const governance = await fetchGovernanceById(slug)

    if (governance && governance?.organization) {
      return {
        redirect: {
          permanent: false,
          destination: ROUTES.governance.proposals.list(
            governance.organization.slug,
          ),
        },
      }
    }
  }

  if (isWhitelabel(context)) {
    if (!isCorrectSlug(context, slug)) {
      return {
        redirect: {
          permanent: false,
          destination: "/404",
        },
      }
    }
  }

  const organization = await fetchOrganizationBySlug(slug, context)

  if (!organization) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const governors = await fetchGovernorsByOrganization(organization.id, context)

  if (!governors.length) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const [governor] = governors

  const { descriptionExceptions } =
    await fetchDescriptionExceptionsByGovernorId(organization.governorIds)

  return {
    props: {
      chainId: governor.chainId,
      organization,
      governors,
      isWhiteLabel: isWhitelabel(context),
      descriptionExceptions,
    },
  }
}

export default ProposalsList
